import * as React from 'react';
import {
  FollowStatusesEnum,
  useGetUserVenueFollowLazyQuery,
  useUpsertVenueFollowMutation,
} from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';

interface Params {
  venueId?: string;
  onNeedsAuth: () => void;
}

/**
 * Venue follow state for the court page. "Add to favorite" and "Follow" both
 * represent the same underlying VenueFollows row (favorites are venue follows).
 */
export const useVenueFollow = ({ venueId, onNeedsAuth }: Params) => {
  const viewer = useViewer();
  const [getUserVenueFollowLazyQuery, { data: followQueryData, loading: isFollowQueryLoading }] =
    useGetUserVenueFollowLazyQuery();
  const [upsertVenueFollowMutation, { loading: isFollowUpsertLoading }] =
    useUpsertVenueFollowMutation();

  const isFollowing = followQueryData?.venueFollows?.[0]?.status === FollowStatusesEnum.Active;
  const isFollowDisabled = viewer.isSessionLoading || isFollowUpsertLoading || isFollowQueryLoading;

  React.useEffect(() => {
    if (viewer.userId && venueId) {
      getUserVenueFollowLazyQuery({
        variables: {
          userId: viewer.userId,
          venueId,
        },
      });
    }
  }, [viewer.userId, venueId]);

  const setFollowStatus = (userId: string, status: FollowStatusesEnum) => {
    if (!venueId) {
      return Promise.resolve();
    }

    return upsertVenueFollowMutation({
      variables: {
        venueId,
        userId,
        status,
      },
    }).then(() =>
      getUserVenueFollowLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          userId,
          venueId,
        },
      }),
    );
  };

  const toggleFollow = () => {
    if (viewer.isSessionLoading || isFollowUpsertLoading || isFollowQueryLoading) {
      return;
    }

    if (viewer.isAnonymousSession || !viewer.userId) {
      onNeedsAuth();
      return;
    }

    setFollowStatus(
      viewer.userId,
      isFollowing ? FollowStatusesEnum.Inactive : FollowStatusesEnum.Active,
    );
  };

  // Used by the signup modal success handler to auto-follow after account creation.
  const followAsUser = (userId: string) => setFollowStatus(userId, FollowStatusesEnum.Active);

  return { isFollowing, isFollowDisabled, toggleFollow, followAsUser };
};
